import React, { useState, useEffect } from 'react'

import { useLocation } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'
import * as Sentry from '@sentry/react'

import AuthSupplierContext from 'contexts/AuthSupplierContext'
import { routes } from 'SupplierRoutes'
import { getMixPanelAuthKey } from 'service/env'
import * as service from 'service'
import useFetch from 'hooks/useFetch'

const AuthSupplierProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [loadData, setLoadData] = useState(false)
  const [companySupplier, setCompanySupplier] = useState(null)
  const [user, setUser] = useState(null)
  const [company, setCompany] = useState(null)
  const [supplier, setSupplier] = useState(null)
  const [permissions, setPermissions] = useState(null)
  const [homeRoute, setHomeRoute] = useState()

  const { pathname } = useLocation()

  const {
    response,
    isLoading: isCompanySupplierLoading,
    isLoadingStart,
    refresh,
  } = useFetch(
    service.supplierPortal.companySuppliers.identify,
    {},
    [loadData],
    service.supplierPortal.auth.isAuthenticated() && loadData,
  )

  const loaded = !isCompanySupplierLoading && !isLoading && !isLoadingStart

  const loadOut = () => {
    setIsLoading(false)
    setUser(null)
    setSupplier(null)
    setPermissions(null)
    setHomeRoute()
  }

  const mixpanelBrowser = () => {
    mixpanel.init(getMixPanelAuthKey(), {
      debug: false, // TRUE somente em um ambiente de desenvolvimento
      ignore_dnt: true,
      secure_cookie: true,
      cross_site_cookie: false,
      api_transport: 'XHR',
      persistence: 'cookie',
      batch_requests: false,
    })

    if (!!companySupplier) {
      mixpanel.register_once({
        supplierDocument: companySupplier?.supplier?.document,
        companyDocument: companySupplier?.company?.document,
      })
    }

    setTimeout(() => {
      mixpanel.track(window.document.title, {
        userId: user?.id,
        supplierId: supplier?.id,
        current_path: pathname,
      })
    }, 2000)
  }

  const loadDataWithAnimation = () => setLoadData(true)

  useEffect(() => {
    if (service.supplierPortal.auth.isAuthenticated() && !loadData)
      setLoadData(true)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (window.document.title !== '') {
      mixpanelBrowser()
    }
    // eslint-disable-next-line
  }, [window.document.title])

  useEffect(() => {
    const fetchData = async () => {
      if (!response) return

      setIsLoading(true)

      let responseUser = await service.supplierPortal.users.me()
      const user = responseUser.data.user
      Sentry.setUser(user)

      const responsePermissions =
        await service.supplierPortal.users.permissions()
      const newPermissions = responsePermissions?.data?.profilePermissions || []
      const identifiedCompanySupplier = response?.data?.companySupplier
      const identifiedSupplier = response?.data?.companySupplier?.supplier
      const identifiedCompany = response?.data?.companySupplier?.company

      setCompanySupplier(identifiedCompanySupplier ?? null)
      setSupplier(identifiedSupplier ?? null)
      setCompany(identifiedCompany ?? null)
      setUser(responseUser.data.user)
      setPermissions(newPermissions)
      setHomeRoute(routes.home)

      if (identifiedCompanySupplier) {
        if (mixpanel.get_distinct_id() !== identifiedCompanySupplier?.id) {
          mixpanel.identify(identifiedCompanySupplier?.id)
        }
      }

      setIsLoading(false)
    }

    fetchData()
  }, [response])

  return (
    <AuthSupplierContext.Provider
      value={{
        company,
        companySupplier,
        homeRoute,
        isLoading,
        loadData,
        loadDataWithAnimation,
        loaded,
        loadOut,
        mixpanelBrowser,
        permissions,
        refresh,
        setCompany,
        setCompanySupplier,
        setIsLoading,
        setUser,
        supplier,
        user,
      }}
    >
      {children}
    </AuthSupplierContext.Provider>
  )
}

export default AuthSupplierProvider
